/**
 * Preferences Context
 * Provides team preferences (table columns, etc.) to components
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import PreferencesService from '../services/preferencesService';
import { DEFAULT_COLUMNS } from '../../features/conversion-events/config/eventColumns';
import { DEFAULT_COLUMNS as DEFAULT_TRANSACTION_COLUMNS } from '../../features/conversion-events/config/transactionColumns';
import { DEFAULT_COLUMNS as DEFAULT_SESSIONS_COLUMNS } from '../../features/conversion-events/config/sessionColumns';
import { DEFAULT_COLUMNS as DEFAULT_USERS_COLUMNS } from '../../features/conversion-events/config/userColumns';
import { DEFAULT_GROUP_COLUMNS } from '../../features/conversion-events/config/groupColumns';

const PreferencesContext = createContext(null);

export const PreferencesProvider = ({ children }) => {
  const [eventsTableColumns, setEventsTableColumns] = useState(null);
  const [transactionsTableColumns, setTransactionsTableColumns] = useState(null);
  const [sessionsTableColumns, setSessionsTableColumns] = useState(null);
  const [usersTableColumns, setUsersTableColumns] = useState(null);
  // Groups: keyed by group_key string → column config
  const [groupsTableColumns, setGroupsTableColumns] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load events table columns for a team
   */
  const loadEventsTableColumns = useCallback(async (teamId) => {
    if (!teamId) {
      setEventsTableColumns(DEFAULT_COLUMNS);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const columns = await PreferencesService.getEventsTableColumns(teamId);
      setEventsTableColumns(columns);
    } catch (err) {
      console.error('Error loading events table columns:', err);
      setError(err.message);
      // Fallback to default columns
      setEventsTableColumns(DEFAULT_COLUMNS);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update events table columns for a team
   */
  const updateEventsTableColumns = useCallback(async (teamId, enabled, order) => {
    if (!teamId) {
      throw new Error('Team ID is required');
    }

    setLoading(true);
    setError(null);
    
    try {
      const updatedColumns = await PreferencesService.updateEventsTableColumns(teamId, enabled, order);
      setEventsTableColumns(updatedColumns);
      return updatedColumns;
    } catch (err) {
      console.error('Error updating events table columns:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reset events table columns to default
   */
  const resetEventsTableColumns = useCallback(async (teamId) => {
    return updateEventsTableColumns(teamId, DEFAULT_COLUMNS.enabled, DEFAULT_COLUMNS.order);
  }, [updateEventsTableColumns]);

  /**
   * Get current events table columns (with fallback to default)
   */
  const getEventsTableColumns = useCallback(() => {
    return eventsTableColumns || DEFAULT_COLUMNS;
  }, [eventsTableColumns]);

  /**
   * Load transactions table columns for a team
   */
  const loadTransactionsTableColumns = useCallback(async (teamId) => {
    if (!teamId) {
      setTransactionsTableColumns(DEFAULT_TRANSACTION_COLUMNS);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const columns = await PreferencesService.getTransactionsTableColumns(teamId);
      setTransactionsTableColumns(columns);
    } catch (err) {
      console.error('Error loading transactions table columns:', err);
      setError(err.message);
      setTransactionsTableColumns(DEFAULT_TRANSACTION_COLUMNS);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update transactions table columns for a team
   */
  const updateTransactionsTableColumns = useCallback(async (teamId, enabled, order) => {
    if (!teamId) {
      throw new Error('Team ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const updatedColumns = await PreferencesService.updateTransactionsTableColumns(teamId, enabled, order);
      setTransactionsTableColumns(updatedColumns);
      return updatedColumns;
    } catch (err) {
      console.error('Error updating transactions table columns:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reset transactions table columns to default
   */
  const resetTransactionsTableColumns = useCallback(async (teamId) => {
    return updateTransactionsTableColumns(teamId, DEFAULT_TRANSACTION_COLUMNS.enabled, DEFAULT_TRANSACTION_COLUMNS.order);
  }, [updateTransactionsTableColumns]);

  /**
   * Get current transactions table columns (with fallback to default)
   */
  const getTransactionsTableColumns = useCallback(() => {
    return transactionsTableColumns || DEFAULT_TRANSACTION_COLUMNS;
  }, [transactionsTableColumns]);

  /**
   * Load sessions table columns for a team
   */
  const loadSessionsTableColumns = useCallback(async (teamId) => {
    if (!teamId) {
      setSessionsTableColumns(DEFAULT_SESSIONS_COLUMNS);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const columns = await PreferencesService.getSessionsTableColumns(teamId);
      setSessionsTableColumns(columns);
    } catch (err) {
      console.error('Error loading sessions table columns:', err);
      setError(err.message);
      setSessionsTableColumns(DEFAULT_SESSIONS_COLUMNS);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update sessions table columns for a team
   */
  const updateSessionsTableColumns = useCallback(async (teamId, enabled, order) => {
    if (!teamId) {
      throw new Error('Team ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const updatedColumns = await PreferencesService.updateSessionsTableColumns(teamId, enabled, order);
      setSessionsTableColumns(updatedColumns);
      return updatedColumns;
    } catch (err) {
      console.error('Error updating sessions table columns:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reset sessions table columns to default
   */
  const resetSessionsTableColumns = useCallback(async (teamId) => {
    return updateSessionsTableColumns(teamId, DEFAULT_SESSIONS_COLUMNS.enabled, DEFAULT_SESSIONS_COLUMNS.order);
  }, [updateSessionsTableColumns]);

  /**
   * Get current sessions table columns (with fallback to default)
   */
  const getSessionsTableColumns = useCallback(() => {
    return sessionsTableColumns || DEFAULT_SESSIONS_COLUMNS;
  }, [sessionsTableColumns]);

  /**
   * Load users table columns for a team
   */
  const loadUsersTableColumns = useCallback(async (teamId) => {
    if (!teamId) {
      setUsersTableColumns(DEFAULT_USERS_COLUMNS);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const columns = await PreferencesService.getUsersTableColumns(teamId);
      setUsersTableColumns(columns);
    } catch (err) {
      console.error('Error loading users table columns:', err);
      setError(err.message);
      setUsersTableColumns(DEFAULT_USERS_COLUMNS);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update users table columns for a team
   */
  const updateUsersTableColumns = useCallback(async (teamId, enabled, order) => {
    if (!teamId) {
      throw new Error('Team ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const updatedColumns = await PreferencesService.updateUsersTableColumns(teamId, enabled, order);
      setUsersTableColumns(updatedColumns);
      return updatedColumns;
    } catch (err) {
      console.error('Error updating users table columns:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reset users table columns to default
   */
  const resetUsersTableColumns = useCallback(async (teamId) => {
    return updateUsersTableColumns(teamId, DEFAULT_USERS_COLUMNS.enabled, DEFAULT_USERS_COLUMNS.order);
  }, [updateUsersTableColumns]);

  /**
   * Get current users table columns (with fallback to default)
   */
  const getUsersTableColumns = useCallback(() => {
    return usersTableColumns || DEFAULT_USERS_COLUMNS;
  }, [usersTableColumns]);

  /**
   * Get group table columns for a specific group_key (stored in localStorage per team+key)
   */
  const getGroupsTableColumns = useCallback((groupKey) => {
    const key = groupKey || 'default';
    if (groupsTableColumns[key]) return groupsTableColumns[key];
    // Try localStorage fallback
    try {
      const stored = localStorage.getItem(`groups_columns_${key}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed;
      }
    } catch (_) {}
    return DEFAULT_GROUP_COLUMNS;
  }, [groupsTableColumns]);

  const updateGroupsTableColumns = useCallback(async (groupKey, enabled, order) => {
    const key = groupKey || 'default';
    const config = { enabled, order };
    setGroupsTableColumns(prev => ({ ...prev, [key]: config }));
    try {
      localStorage.setItem(`groups_columns_${key}`, JSON.stringify(config));
    } catch (_) {}
    return config;
  }, []);

  const resetGroupsTableColumns = useCallback(async (groupKey) => {
    const key = groupKey || 'default';
    setGroupsTableColumns(prev => ({ ...prev, [key]: DEFAULT_GROUP_COLUMNS }));
    try {
      localStorage.removeItem(`groups_columns_${key}`);
    } catch (_) {}
    return DEFAULT_GROUP_COLUMNS;
  }, []);

  const value = {
    // State
    eventsTableColumns: getEventsTableColumns(),
    transactionsTableColumns: getTransactionsTableColumns(),
    loading,
    error,

    // Actions
    loadEventsTableColumns,
    updateEventsTableColumns,
    resetEventsTableColumns,
    getEventsTableColumns,
    loadTransactionsTableColumns,
    updateTransactionsTableColumns,
    resetTransactionsTableColumns,
    getTransactionsTableColumns,
    loadSessionsTableColumns,
    updateSessionsTableColumns,
    resetSessionsTableColumns,
    getSessionsTableColumns,
    loadUsersTableColumns,
    updateUsersTableColumns,
    resetUsersTableColumns,
    getUsersTableColumns,
    getGroupsTableColumns,
    updateGroupsTableColumns,
    resetGroupsTableColumns
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};

/**
 * Hook to use preferences context
 */
export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within PreferencesProvider');
  }
  return context;
};

export default PreferencesContext;
